import { create } from "zustand"
import { persist } from "zustand/middleware"

/**
 * View modes for the Global AI drawer when matter cards are present
 * - split: Both chat and results visible (wide drawer)
 * - results: Results only, chat hidden (narrow drawer)
 * - chat: Chat only, results hidden (narrow drawer)
 */
export type ViewMode = "split" | "results" | "chat"

/**
 * UI state for the Global AI drawer
 */
interface GlobalAIState {
  // Drawer open state
  isOpen: boolean
  setOpen: (open: boolean) => void
  toggle: () => void

  // View mode for split pane (split, results-only, chat-only)
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void

  // Selected session ID
  selectedSessionId: string | null
  setSelectedSessionId: (id: string | null) => void

  // Message input
  messageInput: string
  setMessageInput: (value: string) => void
  clearMessageInput: () => void

  // Reset all state
  reset: () => void
}

const initialState = {
  isOpen: false,
  viewMode: "split" as ViewMode,
  selectedSessionId: null as string | null,
  messageInput: "",
}

export const useGlobalAIStore = create<GlobalAIState>()(
  persist(
    (set) => ({
      ...initialState,

      // Drawer
      setOpen: (open) => set({ isOpen: open }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),

      // View mode
      setViewMode: (mode) => set({ viewMode: mode }),

      // Session selection
      setSelectedSessionId: (id) => set({ selectedSessionId: id }),

      // Message input
      setMessageInput: (value) => set({ messageInput: value }),
      clearMessageInput: () => set({ messageInput: "" }),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: "global-ai-storage",
      partialize: (state) => ({
        // Only persist drawer open state, not session
        isOpen: state.isOpen,
      }),
    }
  )
)
